/**
 * Interconnection report section fallbacks (SNT-5).
 *
 * Pure deterministic fallbacks for the free-text fields on the
 * `interconnection` report-type schema (siteRag.ts:1748-1758). Each
 * composer reads from `sed.grid`, `sed.connectivity`,
 * `sed.onSiteInfrastructure` — no AI calls, no I/O.
 *
 * Note: The "interconnection" report type targets fiber / network
 * interconnection (datacenter), NOT power interconnection. The schema
 * field names (fiberAvailability, latencyModeling, ixProximity, etc.)
 * confirm. The power-interconnection narrative belongs to the
 * `power-availability` schema.
 *
 * Contract & composition mirror `feasibility.ts`. Recursion guard R6
 * pinned by `instructionShapeGuard.test.ts -> "fallback recursion guard"`.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import {
  formatAttributedInternetExchangeFromSed,
  formatAttributedDatacenterFacilities,
  resolveEvidenceSourceLabel,
  isVerifiedEvidenceSection,
} from '../sourceAttribution';

/* ───────────────────────── helpers ──────────────────────────── */

/** Distance in km, one decimal — or `[—]` when missing. */
function kmLabel(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '[—]';
  return `${value.toFixed(1)} km`;
}

/** Closest carrier-anchored fiber descriptor. */
function fiberDescriptor(sed: SiteEvidenceDocument): string {
  const cn = sed.connectivity?.data;
  if (!cn) return '[—]';
  const parts: string[] = [];
  if (typeof cn.nearestFiberKm === 'number' && Number.isFinite(cn.nearestFiberKm)) {
    parts.push(`fiber corridor ${cn.nearestFiberKm.toFixed(2)} km`);
  }
  if (cn.fiberProvider) parts.push(`carrier ${cn.fiberProvider}`);
  if (parts.length === 0) return '[—]';
  return parts.join('; ');
}

/** Source-stated construction length; never label it as closest fiber. */
function fiberBuildDescriptor(sed: SiteEvidenceDocument): string | null {
  const value = sed.connectivity?.data?.fiberBuildDistanceFt;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? `Source-stated fiber build to service handoff: ${value} ft. Construction length does not confirm service or route diversity.`
    : null;
}

/* ───────────────────────── composers ────────────────────────── */

/**
 * Interconnection executive summary fallback.
 */
export function interconnectionExecutiveSummaryFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  parts.push(`${site.name} interconnection summary.`);
  const cn = sed.connectivity?.data;
  if (cn) {
    const sub: string[] = [];
    if (cn.nearestIxName) sub.push(`closest IX ${cn.nearestIxName} (${kmLabel(cn.nearestIxDistKm)})`);
    if (typeof cn.facilityCount50km === 'number' && Number.isFinite(cn.facilityCount50km)) {
      sub.push(`${cn.facilityCount50km} DC facilities within 50 km`);
    }
    const fib = fiberDescriptor(sed);
    if (fib !== '[—]') sub.push(fib);
    if (sub.length > 0) parts.push(`Network baseline: ${sub.join('; ')}.`);
  }
  const build = fiberBuildDescriptor(sed);
  if (build) parts.push(build);
  parts.push('Detailed interconnection narrative pending re-run.');
  return parts.join(' ');
}

/**
 * Fiber availability narrative fallback. Reads
 * `sed.connectivity.nearestFiberKm` + `fiberProvider`.
 */
export function fiberAvailabilityFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const fib = fiberDescriptor(sed);
  const build = fiberBuildDescriptor(sed);
  if (fib === '[—]' && !build) return 'Fiber availability narrative pending re-run.';
  return [fib !== '[—]' ? `Closest fiber: ${fib}.` : null, build, 'Cross-connect detail pending re-run.']
    .filter(Boolean).join(' ');
}

/**
 * Carrier diversity narrative fallback. Counts fiber-tagged corridors
 * in the KMZ inventory as a proxy for diversity.
 */
export function carrierDiversityFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const inf = sed.onSiteInfrastructure?.data;
  const cn = sed.connectivity?.data;
  const fiberCorridors = Array.isArray(inf?.corridors)
    ? inf.corridors.filter(c => c.kind === 'fiber').length
    : 0;
  const parts: string[] = [];
  if (fiberCorridors > 0) parts.push(`${fiberCorridors} fiber corridor(s) tagged in KMZ`);
  if (cn?.fiberProvider) parts.push(`primary carrier label ${cn.fiberProvider}`);
  if (parts.length === 0) return 'Carrier diversity narrative pending re-run.';
  return `Carrier indicators: ${parts.join('; ')}. Multi-carrier resilience detail pending re-run.`;
}

/**
 * IX proximity narrative fallback. Reads connectivity IX fields.
 */
export function ixProximityFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  return `${formatAttributedInternetExchangeFromSed(sed.connectivity)} Peering pathway detail pending re-run.`;
}

/**
 * Submarine cable narrative fallback. The SED doesn't carry cable-
 * landing-station provider today; render `[—]`.
 */
export function submarineCableFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Closest cable landing station: [—]. Submarine cable proximity detail pending re-run.';
}

/**
 * Latency modeling narrative fallback. The SED carries IX proximity
 * but not modelled p50 / p99 — render `[—]` and surface the proxy.
 */
export function latencyModelingFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const cn = sed.connectivity?.data;
  if (!cn || !cn.nearestIxName) return 'Latency modeling narrative pending re-run.';
  return `Anchor IX: ${cn.nearestIxName} (${kmLabel(cn.nearestIxDistKm)}). Modelled p50 / p99 latency to peer regions: [—]. Detailed latency narrative pending re-run.`;
}

/**
 * Network resilience narrative fallback. Composes from KMZ fiber +
 * facility density.
 */
export function networkResilienceFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const cn = sed.connectivity?.data;
  const inf = sed.onSiteInfrastructure?.data;
  const parts: string[] = [];
  const fiberCorridors = Array.isArray(inf?.corridors)
    ? inf.corridors.filter(c => c.kind === 'fiber').length
    : 0;
  if (fiberCorridors > 0) parts.push(`${fiberCorridors} KMZ fiber corridor(s)`);
  if (cn && typeof cn.facilityCount50km === 'number' && Number.isFinite(cn.facilityCount50km)) {
    parts.push(`${cn.facilityCount50km} DC facilities within 50 km`);
  }
  if (parts.length === 0) return 'Network resilience narrative pending re-run.';
  return `Resilience indicators: ${parts.join('; ')}. Diverse-path detail pending re-run.`;
}

/**
 * Edge deployment narrative fallback. Reads DC facility density +
 * population proxy (siteResearch carries population summaries when
 * available, but that's free-form — skipped here for grounding
 * discipline).
 */
export function edgeDeploymentFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const cn = sed.connectivity?.data;
  if (!cn) return 'Edge deployment narrative pending re-run.';
  const parts: string[] = [];
  if (typeof cn.facilityCount50km === 'number' && Number.isFinite(cn.facilityCount50km)) {
    parts.push(`${cn.facilityCount50km} DC facilities within 50 km`);
  }
  if (cn.nearestIxName) parts.push(`closest IX ${cn.nearestIxName}`);
  if (parts.length === 0) return 'Edge deployment narrative pending re-run.';
  return `Edge baseline: ${parts.join('; ')}. Edge-pod placement detail pending re-run.`;
}

/**
 * Interconnection conclusion recommendation fallback.
 */
export function interconnectionRecommendationFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Interconnection recommendation pending re-run.';
}
